import { useCallback, useEffect, useState } from "react";
import {
  RefreshCw,
  Activity,
  Target,
  TrendingUp,
  ShieldAlert,
} from "lucide-react";
import { apiGet } from "@/lib/api";

const Stat = ({ label, value, sub }) => (
  <div className="border border-white/10 bg-[#0d1219] p-4">
    <div className="font-mono text-[9px] uppercase tracking-widest text-mute">
      {label}
    </div>
    <div className="mt-2 font-display text-xl font-bold text-white">
      {value}
    </div>
    {sub && (
      <div className="mt-1 font-mono text-[9px] text-mute-dim">
        {sub}
      </div>
    )}
  </div>
);

const Metric = ({ label, value, note }) => (
  <div className="border border-white/10 bg-[#0d1219] p-4">
    <div className="font-mono text-[9px] uppercase tracking-widest text-mute">
      {label}
    </div>
    <div className="mt-2 font-display text-lg font-bold text-white">
      {value}
    </div>
    {note && (
      <div className="mt-1 font-mono text-[9px] leading-relaxed text-mute-dim">
        {note}
      </div>
    )}
  </div>
);

export function ExecutionLab({ symbol = "XAUUSD" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPair, setSelectedPair] = useState(symbol);

  const load = useCallback(async (manual = false) => {
    try {
      if (manual) setRefreshing(true);

      const result = await apiGet("/learning/execution-lab");

      // apiGet returns Axios response.
      // Keep compatibility if apiGet is changed later.
      setData(result?.data ?? result);
      setError(null);
    } catch (err) {
      setError(err?.message || "Failed to load Execution Lab");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();

    const timer = setInterval(() => load(), 30000);

    return () => clearInterval(timer);
  }, [load]);

  const n = (value, digits = 2) =>
    typeof value === "number" ? value.toFixed(digits) : "—";

  if (loading && !data) {
    return (
      <div className="border border-white/10 bg-[#0d1219] p-8 font-mono text-xs text-mute">
        LOADING EXECUTION LAB...
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="border border-sell/30 bg-[#0d1219] p-6">
        <div className="font-mono text-xs font-bold text-sell">
          EXECUTION LAB ERROR
        </div>

        <div className="mt-2 font-mono text-[10px] text-mute">
          {error}
        </div>

        <button
          onClick={() => load(true)}
          className="mt-4 border border-white/10 px-3 py-2 font-mono text-[9px] font-bold text-white"
        >
          RETRY
        </button>
      </div>
    );
  }

  const statistics = data?.statistics || {};
  const availablePairs = Object.keys(statistics);

  const activePair =
    selectedPair && statistics[selectedPair]
      ? selectedPair
      : availablePairs[0] || symbol;

  const selected = statistics?.[activePair] || {};

  const overall = selected?.overall || {};
  const outcome = overall?.outcome || {};
  const mae = overall?.mae || {};
  const mfe = overall?.mfe || {};
  const retest = overall?.retest || {};
  const direct = overall?.direct_move || {};
  const quality = overall?.data_quality || {};

  const model = data?.execution_model || {};
  const policy = data?.data_policy || {};

  const trackingRate =
    overall?.tracking_rate ??
    quality?.tracking_rate ??
    null;

  const minimumSample =
    typeof policy?.minimum_sample_warning === "number"
      ? policy.minimum_sample_warning
      : 30;

  const isCollecting =
    (overall?.tracked ?? 0) < minimumSample;

  return (
    <div
      className="space-y-3"
      data-testid="execution-lab-content"
    >
      {/* HEADER */}
      <div className="flex flex-col gap-3 border border-white/10 bg-[#0d1219] p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center border border-buy/30 bg-buy/5">
            <Activity size={17} className="text-buy" />
          </div>

          <div>
            <div className="font-display text-lg font-bold tracking-wide">
              EXECUTION LAB
            </div>

            <div className="mt-1 font-mono text-[9px] uppercase tracking-widest text-mute">
              Adaptive Execution Research •{" "}
              {data?.version || "MAX_V1"}
            </div>
          </div>
        </div>

        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="flex items-center justify-center gap-2 border border-white/10 px-3 py-2 font-mono text-[9px] font-bold uppercase tracking-wider text-mute hover:text-white disabled:opacity-50"
        >
          <RefreshCw
            size={12}
            className={refreshing ? "animate-spin" : ""}
          />
          Refresh
        </button>
      </div>

      {/* PAIR SELECTOR */}
      <div className="flex flex-col gap-3 border border-white/10 bg-[#0d1219] p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-mute">
            Research Pair
          </div>

          <div className="mt-1 font-display text-sm font-bold text-white">
            {activePair}
          </div>
        </div>

        <select
          value={activePair}
          onChange={(e) => setSelectedPair(e.target.value)}
          className="w-full border border-white/10 bg-[#0a0f15] px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-white outline-none sm:w-auto"
        >
          {availablePairs.map((pair) => (
            <option key={pair} value={pair}>
              {pair}
            </option>
          ))}
        </select>
      </div>

      {/* DATA STATUS */}
      <div className="flex flex-col gap-2 border border-white/10 bg-[#0d1219] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest">
          <span className="h-2 w-2 rounded-full bg-buy" />
          <span className="text-white">
            LIVE LOGGER ACTIVE
          </span>
        </div>

        <div className="font-mono text-[9px] text-mute">
          {activePair} •{" "}
          {isCollecting ? "COLLECTING DATA" : "ANALYZED"}
        </div>
      </div>

      {/* TOP STATS */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label="Signals"
          value={overall?.signals ?? "—"}
          sub={`Tracked ${overall?.tracked ?? 0}`}
        />

        <Stat
          label="Tracking Rate"
          value={
            trackingRate != null
              ? `${n(trackingRate)}%`
              : "—"
          }
          sub="Live execution samples"
        />

        <Stat
          label="Win Rate"
          value={
            outcome?.winrate != null
              ? `${n(outcome.winrate)}%`
              : "—"
          }
          sub={`${outcome?.wins ?? 0}W / ${outcome?.losses ?? 0}L`}
        />

        <Stat
          label="Symbol"
          value={activePair}
          sub="Research focus"
        />
      </div>

      {/* MAE / MFE */}
      <div className="grid gap-3 lg:grid-cols-2">
        <div className="border border-white/10 bg-[#0d1219] p-4">
          <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-widest">
            <ShieldAlert size={13} />
            MAE / MFE
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <Metric
              label="MAE Average"
              value={n(mae?.average)}
              note="Maximum adverse movement"
            />

            <Metric
              label="MFE Average"
              value={n(mfe?.average)}
              note="Maximum favorable movement"
            />

            <Metric
              label="MAE P75"
              value={n(mae?.p75)}
              note="75th percentile"
            />

            <Metric
              label="MFE P75"
              value={n(mfe?.p75)}
              note="75th percentile"
            />
          </div>
        </div>

        {/* RETEST / DIRECT */}
        <div className="border border-white/10 bg-[#0d1219] p-4">
          <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-widest">
            <TrendingUp size={13} />
            Retest / Direct Move
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <Metric
              label="Retest Rate"
              value={
                retest?.rate != null
                  ? `${n(retest.rate)}%`
                  : "—"
              }
              note={`${retest?.count ?? 0} detected`}
            />

            <Metric
              label="Direct Move Rate"
              value={
                direct?.rate != null
                  ? `${n(direct.rate)}%`
                  : "—"
              }
              note={`${direct?.count ?? 0} detected`}
            />
          </div>
        </div>
      </div>

      {/* EXECUTION MODEL */}
      <div className="grid gap-3 lg:grid-cols-3">
        <Metric
          label="Pending Zone"
          value={
            model?.pending_zone ||
            "COLLECTING DATA"
          }
          note={`Entry mode: ${
            model?.entry_mode || "ANALYSIS"
          }`}
        />

        <Metric
          label="Adaptive TP"
          value={
            model?.tp_model ||
            "COLLECTING DATA"
          }
          note="TP modification remains OFF."
        />

        <Metric
          label="Data Quality"
          value={
            quality?.tracking_rate != null
              ? `${n(quality.tracking_rate)}%`
              : "—"
          }
          note={
            isCollecting
              ? `Need more live samples. Minimum ${minimumSample}.`
              : "Execution sample sufficient."
          }
        />
      </div>

      {/* RESEARCH POLICY */}
      <div className="border border-white/10 bg-[#0d1219] p-4">
        <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-widest">
          <Target size={13} />
          Execution Research Policy
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="font-mono text-[10px] text-mute">
            Focus:{" "}
            <span className="text-white">
              XAUUSD / BTCUSD
            </span>
          </div>

          <div className="font-mono text-[10px] text-mute">
            Historical MAE/MFE:{" "}
            <span className="text-white">
              NOT FABRICATED
            </span>
          </div>

          <div className="font-mono text-[10px] text-mute">
            Live logger:{" "}
            <span className="text-buy">
              ACTIVE
            </span>
          </div>

          <div className="font-mono text-[10px] text-mute">
            Execution:{" "}
            <span className="text-buy">
              LOCKED
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
